import type {
    TripDataFieldDef,
    DataColumn,
    DataModel,
    ReportConfig,
    ModelKey,
    DateGranularity,
    CountMode,
} from "../types";
import type { ReportLogicContext } from "./context";
import { granularityLabel } from "./reportingPeriods";
import { columnDataTypeCategory } from "./operatorCatalog";
import { isTemporalColumn, temporalPairIsComparable } from "./temporalPrecision";

const PLACEHOLDER_COLUMNS = new Set(["subcontractor", "county", "state", "city", "zipCode", "cancelReason", "lobCode", "groupCode", "vendor"]);
const TRIP_DATA_COLUMN_ALIASES: Record<string, string[]> = {
    lobCode: ["lobCode", "LOB Code", "LOBCode", "Line of Business Code"],
    groupCode: ["groupCode", "Group Code", "GroupCode"],
    county: ["county", "County"],
    los: ["los", "LOS", "Level of Service", "LevelOfService"],
    subcontractor: ["subcontractor", "Sub Contractor", "SubContractor", "SubContractorName"],
    vendor: ["vendor", "Vendor", "VendorName"],
};
// --- Calculated-field columns -------------------------------------------------------
// A report's calculated fields are added to their data model's column lists as virtual
// columns (logic/calculatedColumns.ts) keyed "calc:<field id>". They are found only by that
// key - never by label or SQL - so a field named like a real column can never be mistaken
// for it, and they are kept out of every list where a catalog column is required (detail
// column grid, distinct-count column, rule "Set" targets, "Set value from a column").
const CALC_COLUMN_KEY_PREFIX = "calc:";
const calcColumnKey = (fieldId: string) => `${CALC_COLUMN_KEY_PREFIX}${fieldId}`;
const isCalculatedColumnKey = (key?: string | null) => String(key || "").startsWith(CALC_COLUMN_KEY_PREFIX);
const calculatedFieldIdFromKey = (key?: string | null) => isCalculatedColumnKey(key) ? String(key).slice(CALC_COLUMN_KEY_PREFIX.length) : "";
const isCalculatedColumn = (column?: DataColumn | null) => !!column?.calculatedFieldId;
const withoutCalculatedColumns = (columns: DataColumn[]) => columns.filter((column) => !isCalculatedColumn(column));

const dataColumnSelectionKey = (column?: DataColumn | null) => column?.dataModelColumnId ? String(column.dataModelColumnId) : column?.key || "";
const findTimestampColumn = (model: DataModel | undefined, selectedKey?: string) => {
    if (!model?.timestampColumns.length) return null;
    const normalizedKey = String(selectedKey || "");
    return model.timestampColumns.find((column) => dataColumnSelectionKey(column) === normalizedKey || column.key === normalizedKey)
        || model.timestampColumns[0];
};
const TRIP_DATA_FIELDS: TripDataFieldDef[] = [
    { key: "line_of_business", column: "lobCode", label: "LOB Code", hasText: true },
    { key: "groupCode", column: "groupCode", label: "Member Group Code", hasText: true },
    { key: "county", column: "PickupCounty", label: "Pickup County", hasText: false },
    { key: "los", column: "los", label: "Level of Service Name", hasText: true },
    // Sub Contractor intentionally hidden because it duplicates Vendor values in Trip Data & Exclusions.
    // { key: "subcontractor", column: "AssignedVendor", label: "Sub Contractor", hasText: true },
    { key: "vendor", column: "vendor", label: "Assigned Vendor", hasText: true },
];

function attachModel(context: ReportLogicContext, report: ReportConfig, modelKey: ModelKey, dataModelId?: number) {
    report.dataModelIds = report.dataModelIds || [];
    if (!report.models.includes(modelKey)) {
        report.models.push(modelKey);
    }
    if (dataModelId && !report.dataModelIds.includes(dataModelId)) {
        report.dataModelIds.push(dataModelId);
    }
    if (!report.modelBasedOn[modelKey]) {
        report.modelBasedOn[modelKey] = dataColumnSelectionKey(context.dataModels[modelKey]?.timestampColumns[0]);
    }
}

function columnsForModel(context: ReportLogicContext, modelKey: ModelKey | "") {
    return modelKey ? context.dataModels[modelKey]?.categoryColumns || [] : [];
}

function timestampColumnsForModel(context: ReportLogicContext, modelKey: ModelKey | "") {
    return modelKey ? context.dataModels[modelKey]?.timestampColumns || [] : [];
}

/**
 * The timestamp columns a "minutes between" condition may pick from.
 *
 * Narrowed by DataModelColumn.IsOtpComparable, which the catalog already carries for
 * exactly this purpose. The fallback matters: catalog rows written before that flag was
 * populated have it unset on every column, and an empty From/To picker would make the
 * feature look broken rather than unconfigured - so a model where NOTHING is marked offers
 * every timestamp instead.
 */
function timeComparableColumnsForModel(context: ReportLogicContext, modelKey: ModelKey | "") {
    const columns = timestampColumnsForModel(context, modelKey);
    const marked = columns.filter((column) => column.isOtpComparable);
    return marked.length ? marked : columns;
}

/**
 * Model-name disambiguation for lists that pool columns across a report's data models.
 *
 * With more than one data model attached, two models can expose a column with the same
 * display label (e.g. both carry "Vendor"), and every cross-model picker then shows two
 * identical entries. Such columns get the owning model's label appended in brackets -
 * "Vendor (Trips)" / "Vendor (Complaints)". Only `label` changes on the returned copy:
 * key, sql and dataModelColumnId are untouched, so saved selections, lookups and the
 * generated SQL are unaffected. Labels that are unique across the report's models are left
 * as they are, so single-model reports and non-clashing columns look exactly as before.
 */
function ambiguousColumnLabels(context: ReportLogicContext, report?: ReportConfig | null) {
    const ambiguous = new Set<string>();
    const modelKeys = report?.models || [];
    if (modelKeys.length < 2) return ambiguous;

    const owners = new Map<string, Set<ModelKey>>();
    modelKeys.forEach((modelKey) => {
        allColumnsForModel(context, modelKey).forEach((column) => {
            const label = normalizeColumnIdentity(column.label);
            if (!label) return;
            const models = owners.get(label) || new Set<ModelKey>();
            models.add(modelKey);
            owners.set(label, models);
        });
    });
    owners.forEach((models, label) => {
        if (models.size > 1) ambiguous.add(label);
    });
    return ambiguous;
}

function withModelLabel<T extends DataColumn>(context: ReportLogicContext, column: T, modelKey: ModelKey | "", ambiguous: Set<string>): T {
    if (!modelKey || !ambiguous.size || !ambiguous.has(normalizeColumnIdentity(column.label))) return column;
    const modelLabel = context.dataModels[modelKey]?.label || modelKey;
    return { ...column, label: `${column.label} (${modelLabel})` };
}

/** Display label for a saved column key, carrying the model name when the label clashes across the report's models. */
function reportColumnDisplayLabel(context: ReportLogicContext, report: ReportConfig, columnKey: string) {
    const column = findReportColumn(context, report, columnKey);
    if (!column) return "";
    return withModelLabel(context, column, column.model, ambiguousColumnLabels(context, report)).label;
}

/**
 * Every column a Categorization condition may be built on, across the report's models.
 *
 * NOT the same as reportCategoryColumns, which is `role === "category"` only. That pool is
 * right for choosing what a rule set BUCKETS BY, and wrong for choosing what its conditions
 * TEST: the live catalog puts date columns in timestampColumns and everything else in
 * visibleColumns, so a category-only pool cannot offer a single date column - which made
 * "compare to a value" and "compare to another column" unable to see them at all, while
 * "minutes between" (which draws on its own timestamp pool) could.
 *
 * Broadening only ADDS options. No saved rule changes meaning, because findReportColumn
 * already resolves across every pool - it also fixes the display of any existing condition
 * on a non-category column, which previously fell back to showing its raw key.
 */
function reportAllColumns(context: ReportLogicContext, report: ReportConfig) {
    const seen = new Set<string>();
    const columns: DataColumn[] = [];
    const ambiguous = ambiguousColumnLabels(context, report);
    report.models.forEach((modelKey) => {
        allColumnsForModel(context, modelKey).forEach((column) => {
            const key = column.dataModelColumnId ? `id:${column.dataModelColumnId}` : normalizeColumnIdentity(`${column.key}|${column.sql}|${column.label}`);
            if (!key || seen.has(key)) return;
            seen.add(key);
            columns.push(withModelLabel(context, column, modelKey, ambiguous));
        });
    });
    return columns;
}

/** The "To" pool for a time difference whose "From" column is already chosen. */
function differenceColumnsForColumn(context: ReportLogicContext, report: ReportConfig, columnKey: string) {
    const all = reportTimeComparableColumns(context, report);
    const left = findReportColumn(context, report, columnKey);
    if (!left) return all;
    return all.filter((column) => temporalPairIsComparable(left, column));
}

/** Timestamp columns across every model attached to the report, de-duplicated by key. */
function reportTimeComparableColumns(context: ReportLogicContext, report: ReportConfig) {
    const seen = new Set<string>();
    const columns: DataColumn[] = [];
    const ambiguous = ambiguousColumnLabels(context, report);
    report.models.forEach((modelKey) => {
        timeComparableColumnsForModel(context, modelKey).forEach((column) => {
            if (!seen.has(column.key)) {
                seen.add(column.key);
                columns.push(withModelLabel(context, column, modelKey, ambiguous));
            }
        });
    });
    return columns;
}

/**
 * The columns a condition on `column` may be compared AGAINST.
 *
 * Restricted to the same data model and to a compatible data-type category: comparing a
 * text column to a date column is not a condition anyone means to write, and offering it
 * produces a row that silently never matches rather than an error. The left column itself
 * is left in the list - "A = A" is a mistake worth flagging in validation rather than
 * hiding, since removing it would just look like the column is missing.
 */
function comparableColumnsForColumn(context: ReportLogicContext, report: ReportConfig, columnKey: string) {
    const left = findReportColumn(context, report, columnKey);
    if (!left) return [];
    const category = columnDataTypeCategory(left);
    const sameCategory = allColumnsForModel(context, left.model).filter((column) => columnDataTypeCategory(column) === category);
    // Within the temporal category the category check is not enough: `date`, `datetime2`,
    // `time` and `datetimeoffset` all report "date", yet `time` against a timestamp has no
    // meaning at all (SQL Server widens `time` using 1900-01-01), and an offset-aware column
    // against an offset-naive one is compared by assuming the naive side is UTC. Both are
    // excluded here rather than being given a default that could be silently wrong - see
    // temporalPairIsComparable.
    const ambiguous = ambiguousColumnLabels(context, report);
    const pool = isTemporalColumn(left) ? sameCategory.filter((column) => temporalPairIsComparable(left, column)) : sameCategory;
    return pool.map((column) => withModelLabel(context, column, left.model, ambiguous));
}

// --- "Set value" from a column: which columns may be the source ----------------------
// Types with no meaningful text form, or that cannot be grouped (text/ntext/image/xml).
const RULE_VALUE_SOURCE_EXCLUDED_TYPES = ["text", "ntext", "image", "xml", "geography", "geometry", "hierarchyid", "sql_variant", "binary", "varbinary", "rowversion", "timestamp"];
const RULE_VALUE_SOURCE_NUMERIC_TYPES = ["int", "bigint", "smallint", "tinyint", "decimal", "numeric", "float", "real", "money", "smallmoney"];

/**
 * Whether a column may be the value source of a "Set value" rule. The rule copies the value
 * into a breakdown or detail output, so the column must be one the report could already show
 * (breakdownable or detail-selectable), must not be sensitive, must have a text form, and must
 * not be a numeric lookup code (its labels live in the lookup, not the row).
 *
 * Flags the catalog did not send (the local DATA_MODELS fallback) are read permissively, the
 * same way isBreakdownableColumn reads them. Mirrors IsRuleValueSourceEligible in
 * ReportSqlBuilder.RuleResults.cs, which refuses an ineligible column independently of this.
 */
function isRuleValueSourceEligible(column?: DataColumn | null) {
    if (!column || column.isSensitive === true || isCalculatedColumn(column)) return false;
    if (column.isBreakdownable === false && column.isDetailSelectable !== true) return false;
    const dataType = String(column.dataType || "").trim().toLowerCase().replace(/\(.*$/, "");
    if (RULE_VALUE_SOURCE_EXCLUDED_TYPES.includes(dataType)) return false;
    return !(column.lookupSourceId && RULE_VALUE_SOURCE_NUMERIC_TYPES.includes(dataType));
}

/** A column resolved on ONE model only - never on another model the report happens to attach. */
function findModelColumn(context: ReportLogicContext, modelKey: ModelKey | "", columnKey: string) {
    if (!modelKey || !columnKey) return null;
    const column = allColumnsForModel(context, modelKey).find((item) => columnMatchesKey(item, columnKey));
    return column ? { ...column, model: modelKey } : null;
}

/**
 * The columns a "Set value" rule on `modelKey` may take its value from. With no model yet
 * (a rule with no conditions chosen), every eligible column across the report's models.
 */
function ruleValueSourceColumns(context: ReportLogicContext, report: ReportConfig, modelKey: ModelKey | "") {
    const ambiguous = ambiguousColumnLabels(context, report);
    if (modelKey) {
        return allColumnsForModel(context, modelKey)
            .filter(isRuleValueSourceEligible)
            .map((column) => withModelLabel(context, column, modelKey, ambiguous));
    }
    return reportAllColumns(context, report).filter(isRuleValueSourceEligible);
}

function isBreakdownableColumn(column: DataColumn) {
    return column.isBreakdownable !== false;
}

function breakdownColumnsForModel(context: ReportLogicContext, modelKey: ModelKey | "") {
    const model = modelKey ? context.dataModels[modelKey] : null;
    const columns = model?.visibleColumns?.length
        ? model.visibleColumns
        : [...(model?.categoryColumns || []), ...(model?.timestampColumns || [])];

    return columns
        .filter(isBreakdownableColumn)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.label.localeCompare(b.label));
}

// Breakdown columns are backend-controlled through DataModelColumn.IsBreakdownable.
// Timestamp breakdowns can still be grouped by an extracted date part - see dateGranularityExpr below.
function extraBreakdownColumnsForModel(context: ReportLogicContext, modelKey: ModelKey | "") {
    return breakdownColumnsForModel(context, modelKey);
}

function isTimestampColumnForModel(context: ReportLogicContext, modelKey: ModelKey | "", key: string) {
    return timestampColumnsForModel(context, modelKey).some((column) => columnMatchesKey(column, key));
}

function breakdownColumnForModel(context: ReportLogicContext, modelKey: ModelKey | "", key: string) {
    if (!modelKey) return null;
    return breakdownColumnsForModel(context, modelKey).find((column) => columnMatchesKey(column, key))
        || columnsForModel(context, modelKey).find((column) => columnMatchesKey(column, key))
        || timestampColumnsForModel(context, modelKey).find((column) => columnMatchesKey(column, key))
        || null;
}

function extraBreakdownAlias(column: DataColumn, granularity?: DateGranularity) {
    // A calculated field's sql is an expression, not a name: alias it by its label instead.
    if (isCalculatedColumn(column)) {
        const name = String(column.label || "Calculated").replace(/[^a-zA-Z0-9_]+/g, "_").replace(/^_+|_+$/g, "") || "Calculated";
        return granularity ? `${name}_${granularityLabel(granularity) || "Category"}` : `${name}Category`;
    }
    if (!granularity) return `${column.sql}Category`;
    const label = granularityLabel(granularity) || "Category";
    return `${column.sql}_${label}`;
}

function normalizeColumnIdentity(value?: string) {
    return String(value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function columnIdentityValues(column?: DataColumn | null) {
    if (!column) return [];
    if (isCalculatedColumn(column)) return [column.key];
    return [
        column.key,
        column.label,
        column.sql,
        dataColumnSelectionKey(column),
        column.dataModelColumnId ? String(column.dataModelColumnId) : "",
    ].filter(Boolean);
}

function columnMatchesKey(column: DataColumn | undefined | null, key: string) {
    const normalizedKey = normalizeColumnIdentity(key);
    if (!column || !normalizedKey) return false;
    return columnIdentityValues(column).some((value) => normalizeColumnIdentity(value) === normalizedKey);
}

function columnsMatch(left?: DataColumn | null, right?: DataColumn | null) {
    if (!left || !right) return false;
    if (left.dataModelColumnId && right.dataModelColumnId && left.dataModelColumnId === right.dataModelColumnId) return true;
    const rightIdentities = columnIdentityValues(right).map(normalizeColumnIdentity).filter(Boolean);
    return columnIdentityValues(left).some((value) => rightIdentities.includes(normalizeColumnIdentity(value)));
}

function findColumnByIdentity(columns: DataColumn[], identities: string[]) {
    const normalizedIdentities = new Set(identities.map(normalizeColumnIdentity).filter(Boolean));
    if (!normalizedIdentities.size) return null;

    return columns.find((column) => columnIdentityValues(column)
        .map(normalizeColumnIdentity)
        .some((identity) => normalizedIdentities.has(identity))) || null;
}

function tripDataColumnForKey(context: ReportLogicContext, columnKey: string, columns = columnsForModel(context, "transportation")) {
    const field = TRIP_DATA_FIELDS.find((item) => item.key === columnKey || item.column === columnKey);
    const identities = field
        ? [field.key, field.column, field.label, ...(TRIP_DATA_COLUMN_ALIASES[field.column] || [])]
        : [columnKey];
    return findColumnByIdentity(columns, identities);
}

function allSelectableColumnsForModel(context: ReportLogicContext, modelKey: ModelKey | "") {
    const model = modelKey ? context.dataModels[modelKey] : null;
    return model
        ? withoutCalculatedColumns(model.categoryColumns.concat(model.otherColumns)).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.label.localeCompare(b.label))
        : [];
}

function allColumnsForModel(context: ReportLogicContext, modelKey: ModelKey | "") {
    const model = modelKey ? context.dataModels[modelKey] : null;
    if (!model) return [];

    const seen = new Set<string>();
    return [
        ...(model.visibleColumns || []),
        ...model.categoryColumns,
        ...model.timestampColumns,
        ...model.otherColumns,
    ].filter((column) => {
        const key = column.dataModelColumnId ? `id:${column.dataModelColumnId}` : normalizeColumnIdentity(`${column.key}|${column.sql}|${column.label}`);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
    }).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.label.localeCompare(b.label));
}

function resolveCountDistinctColumn(context: ReportLogicContext, modelKey: ModelKey | "", countMode: CountMode | undefined, countDistinctColumn: string | undefined) {
    if (countMode !== "distinct" || !countDistinctColumn) return null;
    return allSelectableColumnsForModel(context, modelKey).find((item) => columnMatchesKey(item, countDistinctColumn)) || null;
}

function detailColumnsForModel(context: ReportLogicContext, modelKey: ModelKey | "") {
    const model = modelKey ? context.dataModels[modelKey] : null;
    if (!model) return [];
    // Full Row Detail adds calculated fields through its own "Calculated Columns" panel.
    return withoutCalculatedColumns(model.visibleColumns || [
        ...model.categoryColumns,
        ...model.timestampColumns,
        ...model.otherColumns,
    ]);
}

function findSelectedDetailColumn(columns: DataColumn[], selectedKey: string) {
    return columns.find((column) => columnMatchesKey(column, selectedKey));
}

function allColumnsAcrossModels(context: ReportLogicContext, report: ReportConfig) {
    const seen = new Set<string>();
    const columns: (DataColumn & { model: ModelKey })[] = [];
    const ambiguous = ambiguousColumnLabels(context, report);
    report.models.forEach((modelKey) => {
        (context.dataModels[modelKey]?.categoryColumns || []).forEach((column) => {
            if (!seen.has(column.key)) {
                seen.add(column.key);
                columns.push({ ...withModelLabel(context, column, modelKey, ambiguous), model: modelKey });
            }
        });
    });
    return columns;
}

function reportCategoryColumns(context: ReportLogicContext, report: ReportConfig) {
    const seen = new Set<string>();
    const columns: DataColumn[] = [];
    const ambiguous = ambiguousColumnLabels(context, report);
    report.models.forEach((modelKey) => {
        withoutCalculatedColumns(context.dataModels[modelKey]?.categoryColumns || []).forEach((column) => {
            if (!seen.has(column.key)) {
                seen.add(column.key);
                columns.push(withModelLabel(context, column, modelKey, ambiguous));
            }
        });
    });
    return columns;
}

function columnLabel(context: ReportLogicContext, columnKey: string) {
    for (const model of Object.values(context.dataModels)) {
        const column = model.visibleColumns?.find((item) => columnMatchesKey(item, columnKey))
            || model.categoryColumns.find((item) => columnMatchesKey(item, columnKey))
            || model.timestampColumns.find((item) => columnMatchesKey(item, columnKey))
            || model.otherColumns.find((item) => columnMatchesKey(item, columnKey));
        if (column) {
            return column.label;
        }
    }
    return columnKey || "Metric";
}

function targetFields(context: ReportLogicContext, report?: ReportConfig) {
    const output = [{ key: "category", label: "Category", virtual: true }];
    const seen = new Set(["category"]);
    const modelKeys = report?.models.length ? report.models : Object.keys(context.dataModels);
    const ambiguous = ambiguousColumnLabels(context, report);
    modelKeys.forEach((modelKey) => {
        withoutCalculatedColumns(context.dataModels[modelKey]?.categoryColumns || []).forEach((column) => {
            if (!seen.has(column.key)) {
                seen.add(column.key);
                output.push({ key: column.key, label: withModelLabel(context, column, modelKey, ambiguous).label, virtual: false });
            }
        });
    });
    return output;
}

function modelColumnByKey(context: ReportLogicContext, modelKey: ModelKey, columnKey: string) {
    return findColumnByIdentity(allColumnsForModel(context, modelKey), [columnKey]);
}

function findReportColumn(context: ReportLogicContext, report: ReportConfig, columnKey: string) {
    for (const modelKey of report.models) {
        const model = context.dataModels[modelKey];
        // timestampColumns is searched explicitly rather than relying on visibleColumns to
        // contain it: the live catalog puts every column in visibleColumns, but the local
        // DATA_MODELS fallback does not, so without this a date condition would resolve to
        // no column - and silently emit no SQL - whenever the catalog endpoint is empty.
        const columns = (model?.categoryColumns || []).concat(model?.otherColumns || [], model?.timestampColumns || [], model?.visibleColumns || []);
        const column = columns.find((item) => columnMatchesKey(item, columnKey));
        if (column) return { ...column, model: modelKey };
    }
    return null;
}

function targetFieldDef(context: ReportLogicContext, fieldKey: string, report?: ReportConfig) {
    if (fieldKey === "category") return { key: "category", label: "Category", sql: "Category", virtual: true };
    const modelKeys = report?.models.length ? report.models : Object.keys(context.dataModels);
    for (const modelKey of modelKeys) {
        const column = withoutCalculatedColumns(context.dataModels[modelKey]?.categoryColumns || []).find((item) => columnMatchesKey(item, fieldKey));
        if (column) return { ...column, virtual: false };
    }
    return { key: fieldKey, label: fieldKey || "Category", sql: fieldKey || "Category", virtual: true };
}

export {
    CALC_COLUMN_KEY_PREFIX,
    calcColumnKey,
    isCalculatedColumnKey,
    calculatedFieldIdFromKey,
    isCalculatedColumn,
    withoutCalculatedColumns,
    PLACEHOLDER_COLUMNS,
    TRIP_DATA_COLUMN_ALIASES,
    TRIP_DATA_FIELDS,
    dataColumnSelectionKey,
    findTimestampColumn,
    attachModel,
    columnsForModel,
    timestampColumnsForModel,
    isBreakdownableColumn,
    breakdownColumnsForModel,
    extraBreakdownColumnsForModel,
    isTimestampColumnForModel,
    breakdownColumnForModel,
    extraBreakdownAlias,
    normalizeColumnIdentity,
    columnIdentityValues,
    columnMatchesKey,
    columnsMatch,
    findColumnByIdentity,
    tripDataColumnForKey,
    allSelectableColumnsForModel,
    allColumnsForModel,
    resolveCountDistinctColumn,
    detailColumnsForModel,
    findSelectedDetailColumn,
    allColumnsAcrossModels,
    reportCategoryColumns,
    reportAllColumns,
    columnLabel,
    reportColumnDisplayLabel,
    targetFields,
    modelColumnByKey,
    findReportColumn,
    targetFieldDef,
    timeComparableColumnsForModel,
    reportTimeComparableColumns,
    comparableColumnsForColumn,
    differenceColumnsForColumn,
    isRuleValueSourceEligible,
    findModelColumn,
    ruleValueSourceColumns,
};
